/**
 * The tenant's absence vocabulary (domain-model-transition P7a, worklist W6).
 *
 * Shape borrowed from `0043`'s billing-categories catalog: `active` + `sort_order`, unique per
 * tenant, so a code RETIRES without rewriting the history that used it.
 *
 * THERE IS NO DELETE, and that is the point rather than an omission. Person-days reference these
 * rows; deleting one destroys the reason a recorded day says it happened. `active = false` is the
 * retirement verb, and a retired code stays visible here so it can be brought back.
 *
 * THE CODE IS NOT EDITABLE after creation, for the same reason: days reference the row, and a code
 * that can be rewritten is a label wearing a key's clothes. Rename the LABEL instead — the days
 * keep reading, because they reference the id.
 *
 * Writes gate on permish {1}, matching the server. Reads are open to the tenant, because everyone
 * who records a day picks from this list.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import { PersonDayService, type AbsenceReason } from '@frontend/AppSettings/services/PipelineProjectService';

export default function AbsenceReasonsPanel() {
  const queryClient = useQueryClient();
  const currentEmployee = useCurrentEmployee();
  const rawPermish = currentEmployee?.permish_id ?? null;
  const permish = typeof rawPermish === 'string' ? parseInt(rawPermish, 10) : rawPermish;
  // The SERVER's write predicate. Both mutations here are @require_permish({1}); the read is open
  // to the tenant. Gating on anything looser hands a permish-2 bid admin a complete editor that
  // 403s on every action — the ProjectSitesTab mismatch this phase inherited an obligation to
  // avoid, and the very thing DMT-L2-TS7A-08 exists to catch.
  const mayManage = permish === 1;

  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reasons = useQuery({
    queryKey: ['AbsenceReasons', 'all'],
    queryFn: () => PersonDayService.listAbsenceReasons(false),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['AbsenceReasons'] });

  const add = useMutation({
    mutationFn: () => PersonDayService.createAbsenceReason({
      code: code.trim(), label: label.trim(),
      sort_order: (reasons.data?.length ?? 0) * 10 + 10,
    }),
    onSuccess: () => { setCode(''); setLabel(''); setError(null); void refresh(); },
    // The server's sentence verbatim — it names the duplicate and says to reactivate instead.
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not add the reason.'),
  });

  const setActive = useMutation({
    mutationFn: (vars: { reason: AbsenceReason; active: boolean }) =>
      PersonDayService.updateAbsenceReason(vars.reason.id, { active: vars.active }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not update the reason.'),
  });

  const rename = useMutation({
    mutationFn: (vars: { id: string; label: string }) =>
      PersonDayService.updateAbsenceReason(vars.id, { label: vars.label }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not rename the reason.'),
  });

  const rows = reasons.data ?? [];
  const canSubmit = code.trim().length > 0 && label.trim().length > 0 && !add.isPending;

  return (
    <div className="bid-admin-defaults">
      <p className="bid-admin-hint">
        The reasons your supervisors pick from when a person&apos;s day is an absence. Retire one
        with Deactivate — it stops being offered, and the days that already used it keep reading.
      </p>

      {reasons.isLoading && <p className="bid-admin-empty">Loading…</p>}
      {reasons.isError && rows.length === 0 && (
        <p className="bid-admin-empty">The absence reasons could not be loaded.</p>
      )}
      {reasons.isError && rows.length > 0 && (
        <p className="bid-admin-empty">
          These could not be refreshed and may be out of date.
        </p>
      )}
      {reasons.isSuccess && rows.length === 0 && (
        <p className="bid-admin-empty">No absence reasons yet.</p>
      )}

      {rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">Label</th>
              <th scope="col">Status</th>
              {mayManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((reason) => (
              <tr key={reason.id}>
                {/* Not editable, deliberately — see the header comment. */}
                <th scope="row">{reason.code}</th>
                <td>
                  {mayManage ? (
                    <input
                      defaultValue={reason.label}
                      aria-label={`Label for ${reason.code}`}
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        if (next && next !== reason.label) {
                          rename.mutate({ id: reason.id, label: next });
                        }
                      }}
                    />
                  ) : reason.label}
                </td>
                <td>{reason.active ? 'Active' : 'Retired'}</td>
                {mayManage && (
                  <td>
                    <button
                      type="button"
                      className="bids-walk-btn"
                      disabled={setActive.isPending && setActive.variables?.reason.id === reason.id}
                      onClick={() => setActive.mutate({ reason, active: !reason.active })}
                    >
                      {reason.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayManage ? (
      <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) add.mutate(); }}>
        <label>
          Code
          <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={40} />
        </label>
        <label>
          Label
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} />
        </label>
        <button type="submit" className="bids-walk-btn" disabled={!canSubmit}>
          {add.isPending ? 'Adding…' : 'Add reason'}
        </button>
      </form>
      ) : (
        <p className="bid-admin-hint">
          Adding or retiring a reason needs system-administrator access.
        </p>
      )}

      {error && <p className="bid-admin-error" role="alert">{error}</p>}
    </div>
  );
}
